import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Box, Skeleton } from '@mui/material';
import { styled } from '@mui/system';
import TopBar from './TopBar';
import Schema from './Schema';
import { useNavigate } from 'react-router';
import { parse as parseYaml } from 'yaml';
import { send_request } from '../../utils/Request';
import { fetchWorkflowDefinitionYaml } from '../../api/workflowDefinition';
import { formatCanonicalWorkflowLogicYaml } from '../../utils/workflowDefinitionYaml';

function getWorkflowName(yaml) {
  try {
    return parseYaml(yaml)?.spec?.name || null;
  } catch {
    return null;
  }
}

const PageContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  minHeight: 0,
  overflowY: 'auto',
  overflowX: 'auto',
  backgroundColor: theme.palette.background.default,
}));

const ContentWrapper = styled(Box)({
  maxWidth: '1600px',
  margin: '0 auto',
  padding: '16px 20px',
});

const HeaderSection = styled(Box)({
  marginBottom: '16px',
});

const DiffSection = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: '12px',
  border: `1px solid ${theme.palette.divider}`,
  overflow: 'hidden',
}));

const LoadingContainer = styled(Box)({
  maxWidth: '1600px',
  margin: '0 auto',
  padding: '16px 20px',
});

const SkeletonHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  marginBottom: '24px',
});

const SkeletonCard = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: '12px',
  border: `1px solid ${theme.palette.divider}`,
  padding: '24px',
}));

function WorkflowSchema() {
  const projectId = window.location.pathname.split('/').pop();
  const navigate = useNavigate();

  // Loading states
  const [loading, setLoading] = useState(true);
  const [compareLoad, setCompareLoad] = useState(false);

  // Data
  const [allProjects, setAllProjects] = useState(null);
  const [workflowName, setWorkflowName] = useState(null);

  // View mode
  const [view, setView] = useState('split_view');
  const [showMetadata, setShowMetadata] = useState(false);

  // Selections
  const [selectionOne, setSelectionOne] = useState(null);
  const [selectionTwo, setSelectionTwo] = useState(null);

  // Canonical workflow definitions
  const [yamlOne, setYamlOne] = useState(null);
  const [yamlTwo, setYamlTwo] = useState(null);

  // Cache definitions to avoid duplicate calls
  const definitionCache = useRef({});

  // Defer diff rendering until stable
  const [deferredRender, setDeferredRender] = useState(false);

  useEffect(() => {
    const initialiseWorkflowSchema = async () => {
      setLoading(true);

      const URL = `project-service/project/all_project_versions/${projectId}`;
      let json = await send_request(URL, null, null, 'GET');
      if (!json || json.status !== 200) return;

      let projects = json.data;
      setAllProjects(projects);
      if (!Array.isArray(projects) || projects.length === 0) {
        setLoading(false);
        return;
      }

      // Check query params
      const params = new URLSearchParams(location.search);
      const selected = params.get('selected');
      const compareWith = params.get('compareWith');

      if (selected && compareWith) {
        const v1 = projects.find(item => String(item.version) === String(selected));
        const v2 = projects.find(item => String(item.version) === String(compareWith));

        if (!v1 || !v2) {
          await handleDefaultCase(projects);
          return;
        }

        setSelectionOne(`${v1.projectStatus}_version_${v1.version}`);
        setSelectionTwo(`${v2.projectStatus}_version_${v2.version}`);
        setLoading(false);

        await handleCompareYaml(selected, compareWith);
        return;
      } else if (selected) {
        const v1 = projects.find(item => String(item.version) === String(selected));

        if (!v1) {
          await handleDefaultCase(projects);
          return;
        }

        setSelectionOne(`${v1.projectStatus}_version_${v1.version}`);
        setSelectionTwo(`${v1.projectStatus}_version_${v1.version}`);
        setLoading(false);

        await handleCompareYaml(selected, selected);
        return;
      }

      await handleDefaultCase(projects);
    };
    initialiseWorkflowSchema();
  }, []);

  const handleDefaultCase = async projects => {
    // Default: compare latest with previous version (if exists)
    const latestProject = projects[projects.length - 1];
    const previousProject = projects.length > 1 ? projects[projects.length - 2] : latestProject;

    const [previousYaml, latestYaml] = await loadWorkflowYamlPair(
      previousProject.version,
      latestProject.version
    );
    if (!latestYaml) return;

    setWorkflowName(getWorkflowName(latestYaml));

    setSelectionOne(`${previousProject.projectStatus}_version_${previousProject.version}`);
    setSelectionTwo(`${latestProject.projectStatus}_version_${latestProject.version}`);

    setYamlOne(previousYaml);
    setYamlTwo(latestYaml);

    // Update URL
    const params = new URLSearchParams(window.location.search);
    params.set('selected', previousProject.version);
    params.set('compareWith', latestProject.version);
    navigate({ search: params.toString() }, { replace: true });

    setLoading(false);

    // Defer diff render for smoother UX
    setTimeout(() => setDeferredRender(true), 50);
  };

  const goBack = () => {
    navigate(`/project/canvas/${projectId}`);
  };

  const getWorkflowYaml = useCallback(async (projectId, version) => {
    const cacheKey = `${projectId}_${version}`;

    // Return cached version if available
    if (definitionCache.current[cacheKey]) {
      return definitionCache.current[cacheKey];
    }

    const definition = await fetchWorkflowDefinitionYaml(projectId, {
      version,
    });
    const yaml = definition.yaml;

    // Cache the result
    if (yaml) {
      definitionCache.current[cacheKey] = yaml;
    }

    return yaml;
  }, []);

  const loadWorkflowYamlPair = async (versionOne, versionTwo) => {
    if (String(versionOne) === String(versionTwo)) {
      const yaml = await getWorkflowYaml(projectId, versionOne);
      return [yaml, yaml];
    }
    return Promise.all([
      getWorkflowYaml(projectId, versionOne),
      getWorkflowYaml(projectId, versionTwo),
    ]);
  };

  const handleCompareYaml = async (selectedV1, selectedV2) => {
    try {
      setCompareLoad(true);
      setDeferredRender(false);

      const versionOne = selectedV1 ? selectedV1 : selectionOne.split('_')[2];
      const versionTwo = selectedV2 ? selectedV2 : selectionTwo.split('_')[2];

      const [firstYaml, secondYaml] = await loadWorkflowYamlPair(
        versionOne,
        versionTwo
      );
      setYamlOne(firstYaml);
      setYamlTwo(secondYaml);
      if (!workflowName) {
        setWorkflowName(getWorkflowName(secondYaml));
      }

      // Update URL params
      if (!selectedV1 && !selectedV2) {
        const params = new URLSearchParams(window.location.search);
        params.set('selected', versionOne);
        params.set('compareWith', versionTwo);
        navigate({ search: params.toString() }, { replace: true });
      }

      setCompareLoad(false);

      // Defer diff render for smoother UX
      setTimeout(() => setDeferredRender(true), 50);
    } catch (e) {
      console.error('Error comparing versions:', e);
      setCompareLoad(false);
    }
  };

  // Extract version info for display
  const versionInfo = useMemo(() => {
    if (!allProjects || !selectionOne || !selectionTwo) return null;

    const v1Num = selectionOne.split('_')[2];
    const v2Num = selectionTwo.split('_')[2];

    const v1Project = allProjects.find(p => String(p.version) === v1Num);
    const v2Project = allProjects.find(p => String(p.version) === v2Num);

    return { v1: v1Project, v2: v2Project };
  }, [allProjects, selectionOne, selectionTwo]);

  const renderSkeleton = () => (
    <LoadingContainer>
      <SkeletonHeader>
        <Skeleton
          variant="circular"
          width={40}
          height={40}
          sx={{ bgcolor: 'action.hover', flexShrink: 0 }}
        />
        <Skeleton
          variant="rounded"
          width={200}
          height={32}
          sx={{ bgcolor: 'action.hover', borderRadius: '8px' }}
        />
      </SkeletonHeader>

      <SkeletonCard sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Skeleton
            variant="rounded"
            width={280}
            height={80}
            sx={{ bgcolor: 'action.hover', borderRadius: '8px' }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', px: 2 }}>
            <Skeleton variant="circular" width={24} height={24} sx={{ bgcolor: 'action.hover' }} />
          </Box>
          <Skeleton
            variant="rounded"
            width={280}
            height={80}
            sx={{ bgcolor: 'action.hover', borderRadius: '8px' }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Skeleton
            variant="rounded"
            width={120}
            height={40}
            sx={{ bgcolor: 'action.hover', borderRadius: '8px' }}
          />
          <Skeleton
            variant="rounded"
            width={100}
            height={40}
            sx={{ bgcolor: 'action.hover', borderRadius: '8px' }}
          />
        </Box>
      </SkeletonCard>

      <SkeletonCard>
        <Skeleton
          variant="rounded"
          width="100%"
          height={600}
          sx={{ bgcolor: 'action.hover', borderRadius: '8px' }}
        />
      </SkeletonCard>
    </LoadingContainer>
  );

  if (loading) {
    return (
      <PageContainer>
        {renderSkeleton()}
      </PageContainer>
    );
  }

  const displayYamlOne = showMetadata
    ? yamlOne
    : formatCanonicalWorkflowLogicYaml(yamlOne);
  const displayYamlTwo = showMetadata
    ? yamlTwo
    : formatCanonicalWorkflowLogicYaml(yamlTwo);

  return (
    <PageContainer>
      <ContentWrapper>
        <HeaderSection>
          <TopBar
            goBack={goBack}
            workflowName={workflowName}
            allProjects={allProjects}
            handleCompareYaml={handleCompareYaml}
            selectionOne={selectionOne}
            selectionTwo={selectionTwo}
            setSelectionOne={setSelectionOne}
            setSelectionTwo={setSelectionTwo}
            compareLoad={compareLoad}
            view={view}
            setView={setView}
            showMetadata={showMetadata}
            setShowMetadata={setShowMetadata}
          />
        </HeaderSection>

        <DiffSection>
          <Schema
            yamlOne={displayYamlOne}
            yamlTwo={displayYamlTwo}
            view={view}
            compareLoad={compareLoad}
            deferredRender={deferredRender}
            versionInfo={versionInfo}
          />
        </DiffSection>
      </ContentWrapper>
    </PageContainer>
  );
}

export default WorkflowSchema;
